import React, { useEffect, useState, useCallback } from 'react';
import {
  securityExceptionService,
  NacExceptionRequest,
  ExceptionCompany,
  NAC_EXCEPTION_ITEMS,
} from '../../services/security-exception.service';
import { useAuth } from '../../contexts/AuthContext';
import BulkUploadBar from '../admin/common/BulkUploadBar';
import { EXCEPTION_SAMPLE_CSV } from '../../lib/exceptionSampleCsv';
import { isAllowedActor } from '../../lib/exceptionOptions';
import { exceptionActorService } from '../../services/exception-actor.service';
import { ActorRejection, toActorRejection } from '../../lib/actorRejection';
import {
  SectionHeader, SearchBar, FormCard, Fieldset, Field, TableShell,
  SortableTh, Th, Row, Td, RowActions, LoadingState, EmptyState, Pagination,
  Input, Select, Textarea,
} from '../ui';

const PAGE_LIMIT = 50;

const EMPTY_FORM: NacExceptionRequest = {
  company: '',
  department: '',
  name: '',
  ip: '',
  exceptionItem: '',
  applyDate: '',
  startDate: '',
  endDate: '',
  reason: '',
  actor: '',
  applyGroup: '',
  approvalDocNo: '',
  remark: '',
  actionType: '수정',
};

interface NacExRow extends NacExceptionRequest {
  id: number;
  createdAt?: string;
}

/** ISO 문자열('2026-07-31T00:00:00.000Z')을 date 입력이 인식하는 'YYYY-MM-DD'로 정규화 */
const toDateInput = (v?: string | null): string => (v ? String(v).slice(0, 10) : '');

/**
 * IPT-598 — 서버 응답 행에는 UpdateNacExceptionDto에 없는 id/createdAt/updatedAt/solution이 섞여 있다.
 * 이를 그대로 PATCH 본문에 실으면 전역 ValidationPipe(whitelist + forbidNonWhitelisted)가
 * 'property id should not exist' 400으로 요청 전체를 거부해 수정 저장이 실패한다.
 * → DTO 허용 필드만 명시적으로 골라 전송한다.
 */
const toDto = (form: NacExceptionRequest): NacExceptionRequest => ({
  company: form.company,
  department: form.department,
  name: form.name,
  ip: form.ip,
  exceptionItem: form.exceptionItem,
  applyDate: form.applyDate,
  startDate: form.startDate,
  endDate: form.endDate,
  reason: form.reason,
  actor: form.actor,
  applyGroup: form.applyGroup,
  approvalDocNo: form.approvalDocNo,
  exceptionContent: form.exceptionContent,
  remark: form.remark,
  actionType: form.actionType,
});

const NacExceptionSection: React.FC = () => {
  const { user } = useAuth();
  const isAdminOrEditor = user?.role === 'admin' || user?.role === 'editor';

  const [rows, setRows] = useState<NacExRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [companies, setCompanies] = useState<ExceptionCompany[]>([]);

  const [filterCompany, setFilterCompany] = useState('');
  const [filterName, setFilterName] = useState('');
  const [sortKey, setSortKey] = useState('');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  const [showForm, setShowForm] = useState(false);
  const [editingRow, setEditingRow] = useState<NacExRow | null>(null);
  const [form, setForm] = useState<NacExceptionRequest>(EMPTY_FORM);
  const [isPermanent, setIsPermanent] = useState(false);
  // 처리자 후보는 DB 마스터에서 받아온다 (관리자 화면에서 추가·삭제)
  const [actorOptions, setActorOptions] = useState<string[]>([]);
  // IPT-613 — 처리자 값 때문에 업로드가 통째로 거부된 경우
  const [actorReject, setActorReject] = useState<ActorRejection | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_LIMIT));

  const load = useCallback(
    async (targetPage = page) => {
      setIsLoading(true);
      try {
        const result = await securityExceptionService.getAll({
          solution: 'NAC',
          company: filterCompany || undefined,
          name: filterName || undefined,
          page: targetPage,
          limit: PAGE_LIMIT,
          sortKey: sortKey || undefined,
          sortDir,
        });
        setRows(result.items as NacExRow[]);
        setTotal(result.total);
      } catch {
        setMessage({ type: 'error', text: '목록을 불러오는 데 실패했습니다.' });
      } finally {
        setIsLoading(false);
      }
    },
    [page, filterCompany, filterName, sortKey, sortDir],
  );

  const handleSort = (key: string) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
    setPage(1);
  };

  useEffect(() => {
    securityExceptionService.getCompanies().then(setCompanies).catch(() => {});
    exceptionActorService
      .getAll()
      .then((list) => setActorOptions(list.map((a) => a.name)))
      .catch(() => setActorOptions([]));
  }, []);

  useEffect(() => {
    setPage(1);
    load(1);
  }, [filterCompany, filterName]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    load(1);
  }, [sortKey, sortDir]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    load(page);
  }, [page]); // eslint-disable-line react-hooks/exhaustive-deps

  const openCreate = () => {
    setEditingRow(null);
    setForm(EMPTY_FORM);
    setIsPermanent(false);
    setShowForm(true);
    setMessage(null);
  };

  const rowToForm = (row: NacExRow): NacExceptionRequest => ({
    company: row.company ?? '',
    department: row.department ?? undefined,
    name: row.name ?? '',
    ip: row.ip ?? '',
    exceptionItem: row.exceptionItem ?? '',
    applyDate: toDateInput(row.applyDate),
    startDate: toDateInput(row.startDate),
    endDate: toDateInput(row.endDate),
    reason: row.reason ?? '',
    actor: row.actor ?? '',
    applyGroup: row.applyGroup ?? '',
    approvalDocNo: row.approvalDocNo ?? '',
    exceptionContent: row.exceptionContent ?? undefined,
    remark: row.remark ?? '',
    actionType: row.actionType ?? '수정',
  });

  const openEdit = (row: NacExRow) => {
    setEditingRow(row);
    setForm(rowToForm(row));
    setIsPermanent(!row.endDate);
    setShowForm(true);
    setMessage(null);
  };

  // 복제 — editingRow를 null로 두어 handleSave가 수정이 아니라 신규 등록으로 처리한다
  const openDuplicate = (row: NacExRow) => {
    setEditingRow(null);
    setForm(rowToForm(row));
    setIsPermanent(!row.endDate);
    setShowForm(true);
    setMessage(null);
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('삭제하시겠습니까?')) return;
    try {
      await securityExceptionService.deleteNac(id);
      setMessage({ type: 'success', text: '삭제되었습니다.' });
      load(page);
    } catch {
      setMessage({ type: 'error', text: '삭제에 실패했습니다.' });
    }
  };

  const handleSave = async () => {
    // IPT-613 — 목록 밖 처리자는 저장하지 않는다 (그대로 저장하면 값이 지워진다)
    if (!isAllowedActor(form.actor, actorOptions)) return;
    // IPT-624 — 결재문서번호·적용그룹은 필수 입력
    if (!form.approvalDocNo?.trim() || !form.applyGroup?.trim()) {
      alert('결재문서번호와 적용그룹은 필수 입력 항목입니다.');
      return;
    }

    setIsSaving(true);
    try {
      const dto: NacExceptionRequest = {
        ...toDto(form),
        endDate: isPermanent ? undefined : form.endDate,
      };
      if (editingRow) {
        await securityExceptionService.updateNac(editingRow.id, dto);
        setMessage({ type: 'success', text: '수정되었습니다.' });
      } else {
        await securityExceptionService.createNac(dto);
        setMessage({ type: 'success', text: '등록되었습니다.' });
      }
      setShowForm(false);
      load(page);
    } catch {
      setMessage({ type: 'error', text: '저장에 실패했습니다.' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleUpload = async (file: File) => {
    setMessage(null);
    setActorReject(null);
    try {
      const result = await securityExceptionService.nacExcelUpload(file);
      setMessage({ type: 'success', text: `${result.inserted}건 업로드되었습니다.` });
      load(1);
    } catch (e) {
      // IPT-613 — 처리자 값 오류는 저장 전에 파일 전체가 거부된다. 걸린 행을 그대로 보여준다.
      const rejection = toActorRejection(e);
      if (rejection) setActorReject(rejection);
      else setMessage({ type: 'error', text: '업로드에 실패했습니다.' });
      throw new Error('upload failed');
    }
  };

  const fmt = (d?: string | null) => (d ? d.slice(0, 10) : '-');
  const set = (patch: Partial<NacExceptionRequest>) => setForm((f) => ({ ...f, ...patch }));
  const nacCompanies = companies.filter((c) => c.nacUseYn === 'Y');

  const columns: { label: string; key?: string }[] = [
    { label: '계열사', key: 'company' },
    { label: '이름', key: 'name' },
    { label: 'IP' },
    { label: '예외항목', key: 'exceptionItem' },
    { label: '적용일', key: 'applyDate' },
    { label: '기한' },
    { label: '사유' },
    { label: '처리자' },
    { label: '적용그룹' },
    { label: '결재문서번호' },
    { label: '비고' },
  ];

  return (
    <div>
      <SectionHeader
        title="NAC 예외처리 관리"
        total={total}
        onAdd={isAdminOrEditor ? openCreate : undefined}
        addLabel="+ 예외처리 추가"
        description="네트워크 접근제어(NAC) 예외처리 신청 및 관리"
      />

      {message && (
        <div
          className={
            'mb-4 flex items-center justify-between rounded-md border px-4 py-2.5 text-sm ' +
            (message.type === 'success'
              ? 'border-green-500/40 bg-green-500/10 text-green-700 dark:text-green-400'
              : 'border-destructive/40 bg-destructive/10 text-destructive')
          }
        >
          {message.text}
          <button type="button" onClick={() => setMessage(null)} className="text-base leading-none">×</button>
        </div>
      )}

      {actorReject && (
        <div
          role="alert"
          className="mb-5 rounded-lg border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive"
          style={{ wordBreak: 'keep-all', overflowWrap: 'break-word' }}
        >
          <div className="flex items-start justify-between gap-3">
            <p className="font-semibold">{actorReject.message}</p>
            <button type="button" onClick={() => setActorReject(null)} className="shrink-0 text-sm underline">
              닫기
            </button>
          </div>
          <ul className="mt-2 space-y-1">
            {actorReject.invalidActors.map((r) => (
              <li key={`${r.row}-${r.value}`}>{r.row}행 — ‘{r.value}’</li>
            ))}
          </ul>
          {actorReject.allowedActors.length > 0 && (
            <p className="mt-2 text-xs opacity-80">사용 가능: {actorReject.allowedActors.join(' · ')}</p>
          )}
        </div>
      )}

      {/* 벌크 업로드 — 상단 */}
      {isAdminOrEditor && (
        <div className="mb-5">
          <BulkUploadBar sample={EXCEPTION_SAMPLE_CSV.nac} onUpload={handleUpload} />
        </div>
      )}

      {/* 검색 바 — 하단 */}
      <SearchBar>
        <Select value={filterCompany} onChange={(e) => setFilterCompany(e.target.value)} className="w-auto min-w-[150px]">
          <option value="">전체 계열사</option>
          {nacCompanies.map((c) => <option key={c.orgCode} value={c.companyName}>{c.companyName}</option>)}
        </Select>
        <Input placeholder="이름 검색" value={filterName} onChange={(e) => setFilterName(e.target.value)} className="w-[140px]" />
      </SearchBar>

      {/* 등록/수정 인라인 카드 */}
      {showForm && (
        <FormCard
          title={`NAC 예외처리 ${editingRow ? '수정' : '등록'}`}
          onClose={() => setShowForm(false)}
          onSave={handleSave}
          isSaving={isSaving}
          saveLabel={editingRow ? '수정 저장' : '추가'}
        >
          {/* 목록 밖 처리자 — 어떤 값이 걸렸는지 보여주고 저장을 막는다 */}
          {!isAllowedActor(form.actor, actorOptions) && (
            <div
              role="alert"
              className="mb-4 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900"
              style={{ wordBreak: 'keep-all' }}
            >
              저장된 처리자 <strong>‘{form.actor}’</strong>은(는) 선택할 수 있는 값이 아닙니다.
              아래에서 처리자를 다시 고른 뒤 저장해 주세요.
            </div>
          )}

          <Fieldset legend="예외 항목" accent>
            <Field label="계열사">
              <Select value={form.company || ''} onChange={(e) => set({ company: e.target.value })}>
                <option value="">선택하세요</option>
                {nacCompanies.map((c) => <option key={c.orgCode} value={c.companyName}>{c.companyName}</option>)}
              </Select>
            </Field>
            <Field label="예외항목">
              <Select value={form.exceptionItem || ''} onChange={(e) => set({ exceptionItem: e.target.value })}>
                <option value="">선택하세요</option>
                {NAC_EXCEPTION_ITEMS.map((item) => <option key={item} value={item}>{item}</option>)}
                <option value="재부팅 PC">재부팅 PC</option>
              </Select>
            </Field>
            <Field label="IP">
              <Input value={form.ip || ''} onChange={(e) => set({ ip: e.target.value })} className="font-mono" placeholder="예: 192.168.1.100" />
            </Field>
          </Fieldset>

          <Fieldset legend="신청자 정보">
            <Field label="이름">
              <Input value={form.name || ''} onChange={(e) => set({ name: e.target.value })} placeholder="이름 입력" />
            </Field>
            <Field label="처리자" htmlFor="nac-actor">
              <Select id="nac-actor" value={form.actor || ''} onChange={(e) => set({ actor: e.target.value })}>
                <option value="">선택</option>
                {actorOptions.map((a) => (
                  <option key={a} value={a}>{a}</option>
                ))}
              </Select>
            </Field>
            <Field label="적용그룹" htmlFor="nac-apply-group">
              <Input id="nac-apply-group" value={form.applyGroup || ''} onChange={(e) => set({ applyGroup: e.target.value })} placeholder="정책명 입력" />
            </Field>
            <Field label="결재문서번호" htmlFor="nac-approval-doc-no">
              <Input id="nac-approval-doc-no" value={form.approvalDocNo || ''} onChange={(e) => set({ approvalDocNo: e.target.value })} placeholder="결재문서번호 입력" />
            </Field>
          </Fieldset>

          <Fieldset legend="처리 기간" minColWidth={180}>
            <Field label="예외처리 적용일">
              <Input type="date" value={form.applyDate || ''} onChange={(e) => set({ applyDate: e.target.value })} />
            </Field>
            <Field label="기한 (영구 시 미입력)">
              <div className="flex items-center gap-2">
                <Input
                  type="date"
                  value={form.endDate || ''}
                  onChange={(e) => set({ endDate: e.target.value })}
                  disabled={isPermanent}
                  className={isPermanent ? 'flex-1 opacity-50' : 'flex-1'}
                />
                <label className="flex cursor-pointer items-center gap-1 whitespace-nowrap text-xs">
                  <input
                    type="checkbox"
                    checked={isPermanent}
                    onChange={(e) => { setIsPermanent(e.target.checked); if (e.target.checked) set({ endDate: '' }); }}
                  />
                  영구
                </label>
              </div>
            </Field>
          </Fieldset>

          <Fieldset legend="상세 내용" columns="1fr 1fr">
            <Field label="예외처리 사유">
              <Textarea value={form.reason || ''} onChange={(e) => set({ reason: e.target.value })} rows={3} placeholder="사유 입력" />
            </Field>
            <Field label="비고">
              <Textarea value={form.remark || ''} onChange={(e) => set({ remark: e.target.value })} rows={3} placeholder="비고 입력" />
            </Field>
          </Fieldset>
        </FormCard>
      )}

      {/* 목록 테이블 */}
      {isLoading ? (
        <LoadingState />
      ) : rows.length === 0 ? (
        <EmptyState text="데이터가 없습니다." />
      ) : (
        <TableShell
          headers={
            <>
              {columns.map((c) =>
                c.key ? (
                  <SortableTh key={c.label} label={c.label} sortKey={c.key} activeKey={sortKey} dir={sortDir} onSort={handleSort} />
                ) : (
                  <Th key={c.label}>{c.label}</Th>
                ),
              )}
              {isAdminOrEditor && <Th>관리</Th>}
            </>
          }
          footer={<Pagination page={page} totalPages={totalPages} onPage={(p) => setPage(p)} />}
        >
          {rows.map((row, idx) => (
            <Row key={row.id} idx={idx}>
              <Td className="font-medium">{row.company || '-'}</Td>
              <Td>{row.name || '-'}</Td>
              <Td className="font-mono">{row.ip || '-'}</Td>
              <Td>{row.exceptionItem || '-'}</Td>
              <Td className="whitespace-nowrap">{fmt(row.applyDate)}</Td>
              <Td className="whitespace-nowrap">{row.endDate ? fmt(row.endDate) : '영구'}</Td>
              <Td className="max-w-[180px] overflow-hidden text-ellipsis whitespace-nowrap">{row.reason || '-'}</Td>
              <Td>{row.actor || '-'}</Td>
              <Td>{row.applyGroup || '-'}</Td>
              <Td>{row.approvalDocNo || '-'}</Td>
              <Td className="max-w-[140px] overflow-hidden text-ellipsis whitespace-nowrap">{row.remark || '-'}</Td>
              {isAdminOrEditor && (
                <Td className="whitespace-nowrap">
                  <RowActions
                    onEdit={() => openEdit(row)}
                    onDelete={() => handleDelete(row.id)}
                    onDuplicate={() => openDuplicate(row)}
                  />
                </Td>
              )}
            </Row>
          ))}
        </TableShell>
      )}
    </div>
  );
};

export default NacExceptionSection;
